import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PromotionType } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/Promotion';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { Badge } from 'antd-v5';
import bigDecimal from 'js-big-decimal';
import moment from 'moment';
import { RootState } from 'app/AppStore';
import { CertificateIcon, DiscountCornerIcon } from 'assets/icons';
import {
  CornerLabel,
  DiscountPositionWrapper,
  DiscountTagWrapper,
  FlashPositionWrapper,
  FlashTextWrapper,
} from 'components/Discount/Discount.styled';
import { isFlashPromotion } from 'domains/promotion/Promotion.types';
import { getDiscount } from 'domains/references/References.store';
import type { ReferencePriceType } from 'domains/references/References.types';
import { theme } from 'styles';
import { Box, CenterFlex, Flex, Icon, Text } from 'UI';

const scale_compute = 10;
const scale_final = 2;
const hundred = new bigDecimal('100');
const { Ribbon } = Badge;

type DiscountCornerType = 'cart' | 'catalog' | 'catalog_mini' | 'promotion' | 'product' | 'productList' | 'estimate';

interface DiscountSizeFont {
  type: string;
  offsetY: number;
  offsetX?: number;
  numericOffsets?: Record<string, { x: number; y: number }>;
}

interface DiscountSize {
  icon: number;
  font: DiscountSizeFont;
}

interface DiscountCornerBasicProps {
  discount: ReferenceDiscount | undefined;
  type: DiscountCornerType;
  staticPercent?: number;
  staticType?: PromotionType;
}

interface DiscountCornerProps {
  reference: string;
  type: DiscountCornerType;
  staticPercent?: number;
  staticType?: PromotionType;
}

interface FlashCertificateProps {
  reference: string;
  type: 'product' | 'promotion';
  staticPercent?: number;
}

interface DiscountTagProps {
  reference: string;
}

interface DiscountTagBasicProps {
  discount?: ReferenceDiscount;
  staticPercent?: number;
}

interface ProductDiscountRibbonProps {
  text: string;
}

const ProductDiscountRibbon = ({ text }: ProductDiscountRibbonProps) => (
  <DiscountTagWrapper data-testid={'product-discount-tag'}>
    <Ribbon text={text} color={theme.color.palette_red_7} />
  </DiscountTagWrapper>
);

const discountSizes: Record<DiscountCornerType, DiscountSize> = {
  cart: {
    icon: 60,
    font: { type: 'dark_14_white_100', offsetY: -65 },
  },
  estimate: {
    icon: 55,
    font: { type: 'light_12_dark', offsetY: -65, offsetX: -1 },
  },
  catalog: {
    icon: 110,
    font: {
      type: 'h1_lead_white_singular',
      offsetY: -120,
      numericOffsets: { '1': { x: 4, y: -4 }, '2': { x: -2, y: 0 }, '3': { x: -14, y: 6 }, '4': { x: -18, y: 10 } },
    },
  },
  catalog_mini: {
    icon: 58,
    font: { type: 'light_12_dark', offsetY: -68 },
  },
  promotion: {
    icon: 55,
    font: { type: 'light_12_dark', offsetY: -62 },
  },
  productList: {
    icon: 55,
    font: { type: 'light_12_dark', offsetY: -65 },
  },
  product: {
    icon: 120,
    font: {
      type: 'h1_lead_white_singular',
      offsetY: -135,
      offsetX: -5,
      numericOffsets: { '1': { x: 4, y: 0 }, '2': { x: -2, y: 4 }, '3': { x: -12, y: 10 }, '4': { x: -16, y: 14 } },
    },
  },
};

const flashSizes = {
  product: {
    icon: 96,
    offsetY: 15,
    font: {
      type: 'h1_lead_white_singular',
      offsetY: -80,
    },
  },
  promotion: {
    icon: 48,
    offsetY: 12,
    font: {
      type: 'advertisement_text',
      offsetY: -35,
    },
  },
};

// Linear regression (least squares) over 6 types × 4 numericCounts, all within ±3px of manual values.
// Per-type `numericOffsets` take precedence over this formula when defined.
const computeOffsetsFromIcon = (iconSize: number, numericCount: number): { x: number; y: number } => {
  // prettier-ignore
  const coeffs: Record<number, { ax: number; bx: number; ay: number; by: number }> = {
    1: { ax:  7.93, bx: -0.034,  ay:  2.71, by: -0.0398 },
    2: { ax:  7.85, bx: -0.0854, ay:  0.62, by:  0.0138 },
    3: { ax: 12.35, bx: -0.2186, ay:  0.86, by:  0.063  },
    4: { ax: 10.96, bx: -0.2418, ay:  0.77, by:  0.0991 },
  };
  const c = coeffs[numericCount] ?? coeffs[4];
  return { x: Math.round(c.ax + c.bx * iconSize), y: Math.round(c.ay + c.by * iconSize) };
};

const getNumericCount = (percentText: string) => percentText.split('').filter((ch) => ch >= '0' && ch <= '9').length;

const resolveNumericOffset = (type: DiscountCornerType, numericCount: number) => {
  const { icon, font } = discountSizes[type];
  return font.numericOffsets?.[numericCount.toString()] ?? computeOffsetsFromIcon(icon, numericCount);
};

export const getDiscountTextStyle = (discount: ReferenceDiscount | undefined) => {
  switch (discount?.promotionType) {
    case 'DISCOUNT':
      return 'promoDiscount';
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
    case 'PROMOTION_FLASH_TIME_LIMITED':
      return 'cyanHighlight';
    default:
      return undefined;
  }
};
export const getDiscountColor = (discount: ReferenceDiscount | undefined) => {
  switch (discount?.promotionType) {
    case 'DISCOUNT':
      return theme.color.palette_red_7;
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
    case 'PROMOTION_FLASH_TIME_LIMITED':
      return theme.color.cyan;
    default:
      return theme.color.brand_black;
  }
};

function getPriceWithoutPromotionDiscount(price: ReferencePriceType, vatIncluded: boolean, isUrgent: boolean) {
  const garageView = price.garageView;
  if (vatIncluded) {
    return Number(isUrgent ? garageView?.vatIncludedPriceUrgent : garageView?.vatIncludedPrice);
  } else {
    return Number(isUrgent ? garageView?.vatExcludedPriceUrgent : garageView?.vatExcludedPrice);
  }
}

export function getBasePrice(price: ReferencePriceType, vatIncluded: boolean) {
  return new bigDecimal(
    vatIncluded ? price.clientView?.recommendedPriceVatIncluded : price.clientView?.recommendedPriceVatExcluded,
  );
}

export function calculateUrgentDeliveryAddition(
  price: ReferencePriceType | undefined,
  discount: ReferenceDiscount | undefined,
  vatIncluded: boolean,
) {
  const urgentPriceWithDiscount = calculatePriceAfterDiscount(discount, price, vatIncluded, true);
  const standardPriceWithDiscount = calculatePriceAfterDiscount(discount, price, vatIncluded, false);
  return urgentPriceWithDiscount - standardPriceWithDiscount;
}

export const calculatePriceAfterDiscount = (
  discount: ReferenceDiscount | undefined,
  price: ReferencePriceType | undefined,
  vatIncluded: boolean,
  isUrgent = false,
): number => {
  if (!price) {
    return 0;
  }
  if (!discount) {
    return getPriceWithoutPromotionDiscount(price, vatIncluded, isUrgent);
  }

  const basePrice = getBasePrice(price, vatIncluded);
  const groupDiscount = isUrgent
    ? Number(price.garageView?.discountRateUrgent ?? 0)
    : Number(price.garageView?.discountRate ?? 0);

  switch (discount.discountType) {
    case 'PCL': {
      const joinDiscount = discount.discount + groupDiscount;
      return Number(
        basePrice
          .divide(hundred, scale_compute, bigDecimal.RoundingModes.HALF_UP)
          .multiply(new bigDecimal(100 - Math.min(joinDiscount, 100)))
          .round(scale_final, bigDecimal.RoundingModes.HALF_EVEN)
          .getValue(),
      );
    }
    case 'PNMRA': {
      const partialPrice = basePrice
        .divide(hundred, scale_compute, bigDecimal.RoundingModes.HALF_UP)
        .multiply(new bigDecimal(100 - groupDiscount));
      return Number(
        partialPrice
          .divide(hundred, scale_compute, bigDecimal.RoundingModes.HALF_UP)
          .multiply(hundred.subtract(new bigDecimal(discount.discount)))
          .round(scale_final, bigDecimal.RoundingModes.HALF_EVEN)
          .getValue(),
      );
    }
    default:
      return 0;
  }
};

export const FlashCertificate = ({ reference, type, staticPercent }: FlashCertificateProps) => {
  const { t } = useTranslation();
  const referenceDiscount = useSelector((state: RootState) => getDiscount(state, reference));
  const showUnits = type === 'product';

  const getTimeLeft = (units: 'd' | 'h' | 'm') => moment(referenceDiscount?.validityEnd).diff(moment(), units);

  const daysLeft = getTimeLeft('d');
  const hoursLeft = getTimeLeft('h');
  const minutesLeft = getTimeLeft('m');

  if (!isFlashPromotion(referenceDiscount?.promotionType) && !staticPercent) {
    return null;
  }

  const getUnitsText = () => {
    switch (referenceDiscount?.promotionType) {
      case 'PROMOTION_FLASH_TIME_LIMITED': {
        if (daysLeft > 0) {
          return `${t('backoffice.promotion.days_left', '{{count}} days left.', {
            count: daysLeft,
          })}.`;
        }
        if (hoursLeft > 0) {
          return `${t('backoffice.promotion.hours_left', '{{count}} hours left.', {
            count: hoursLeft,
          })}.`;
        }
        return `${t('backoffice.promotion.minutes_left', '{{count}} minutes left.', {
          count: minutesLeft,
        })}.`;
      }
      case 'PROMOTION_FLASH_QUANTITY_LIMITED':
        return `${t('backoffice.promotion.units_left', '{{count}} units left', {
          count: referenceDiscount?.remainingQuantity,
        })}.`;
    }
  };

  return (
    <Flex direction={'column'} align={'center'}>
      <Icon IconComponent={CertificateIcon} size={flashSizes[type].icon} color={theme.color.cyan} noPointer />
      <FlashPositionWrapper offsetY={flashSizes[type].offsetY}>
        <Text type={flashSizes[type].font.type} disableGutter cursor={'initial'}>
          {`-${staticPercent ?? referenceDiscount?.discount}%`}
        </Text>
      </FlashPositionWrapper>
      {showUnits && (
        <>
          <Box height={17} />
          <FlashTextWrapper>
            <CenterFlex>
              <Text type={'card_title_dark'} disableGutter cursor={'initial'}>
                {getUnitsText()}
              </Text>
            </CenterFlex>
          </FlashTextWrapper>
        </>
      )}
    </Flex>
  );
};

export const DiscountTagBasic = ({ discount, staticPercent }: DiscountTagBasicProps) => {
  const { t } = useTranslation();
  const percentValue = staticPercent ?? discount?.discount;

  if (percentValue === undefined || isFlashPromotion(discount?.promotionType)) {
    return null;
  }

  return (
    <ProductDiscountRibbon
      text={`${t('common.discount.additional_discount', 'Additional discount')} ${percentValue}%`}
    />
  );
};

export const DiscountTag = ({ reference }: DiscountTagProps) => {
  const referenceDiscount = useSelector((state: RootState) => getDiscount(state, reference));
  return <DiscountTagBasic discount={referenceDiscount} />;
};

export const getPromotionColor = ({
  staticType,
  referencePromotionType,
  fallbackColor,
}: {
  staticType?: PromotionType;
  referencePromotionType?: PromotionType;
  fallbackColor?: string;
}) => {
  const promotionType = staticType ?? referencePromotionType;
  switch (promotionType) {
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
    case 'PROMOTION_FLASH_TIME_LIMITED':
      return theme.color.cyan;
    case 'DISCOUNT':
      return theme.color.red_alt;
    default:
      return fallbackColor ?? theme.color.brand_black;
  }
};
export const DiscountCornerBasic = ({ discount, type, staticPercent, staticType }: DiscountCornerBasicProps) => {
  if (!discount && !staticPercent) {
    return null;
  }

  if (type === 'promotion' && isFlashPromotion(staticType)) {
    return null;
  }

  if (type === 'product' && isFlashPromotion(discount?.promotionType)) {
    return null;
  }

  const percentValue = staticPercent ?? discount?.discount;
  const percentText = String(percentValue ?? '');
  const numericCount = getNumericCount(percentText);

  const { offsetY: baseOffsetY, offsetX: baseOffsetX = -5 } = discountSizes[type].font;
  const numericOffset = resolveNumericOffset(type, numericCount);

  const adjustedOffsetX = baseOffsetX + numericOffset.x;
  const adjustedOffsetY = baseOffsetY + numericOffset.y;

  return (
    <DiscountPositionWrapper>
      <Icon
        IconComponent={DiscountCornerIcon}
        noPointer
        size={discountSizes[type].icon}
        color={getPromotionColor({ staticType, referencePromotionType: discount?.promotionType })}
      />
      <CornerLabel offsetY={adjustedOffsetY} offsetX={adjustedOffsetX}>
        <Text type={discountSizes[type].font.type}>{`-${percentText} %`}</Text>
      </CornerLabel>
    </DiscountPositionWrapper>
  );
};

export const DiscountCorner = ({ reference, type, staticPercent, staticType }: DiscountCornerProps) => {
  const referenceDiscount = useSelector((state: RootState) => getDiscount(state, reference));
  return (
    <DiscountCornerBasic
      discount={referenceDiscount}
      type={type}
      staticPercent={staticPercent}
      staticType={staticType}
    />
  );
};
